// Outbound WebSocket payload encoder.
//
// API Gateway WebSocket has a 128 KB per-message ceiling. The chat-history
// payload — specifically the `messages` array carrying the running prompt
// context — grows turn-by-turn and was exceeding this on rich conversations
// (full itinerary JSON + flight + hotel cards). When that happened API
// Gateway silently closed the connection without surfacing an error.
//
// This codec sits in front of every outbound `sendMessage` call. For each body:
//   1. JSON-stringify
//   2. If the raw size is below SMALL_PAYLOAD_THRESHOLD, pass through as-is
//      (no envelope, no compression) — covers pings, small acks, voice-channel
//      messages. Backend sees the same shape it always did. Backward compatible.
//   3. Otherwise gzip-compress, base64-encode, and wrap in a `gzip-b64`
//      envelope. JSON conversation history compresses ~85% — pushes the
//      effective ceiling from 128 KB raw to roughly 900 KB raw.
//   4. If the compressed bytes still exceed MAX_COMPRESSED_BYTES, throw
//      `PayloadTooLargeError`. Caller (SocketContext) surfaces a user-facing
//      message ("this chat is too long, please start a new one") and skips
//      the send. Realistically this never fires — a 1 MB raw payload still
//      compresses to ~125 KB — but it's a clean fail-loud path instead of
//      silently dropping the connection.
//
// Backend protocol contract (handler must support both shapes):
//   - Plain JSON                              → process as before
//   - { encoding: "gzip-b64", payload, ... }  → b64-decode, gunzip, parse, process

import pako from "pako";

// Tunables. All sizes are in BYTES.
//
// Below this raw-JSON size, skip compression entirely. gzip overhead (~20 b
// header + per-block metadata) makes compression worse than useless on tiny
// payloads.
export const SMALL_PAYLOAD_THRESHOLD = 4 * 1024;
//
// Hard ceiling on the compressed bytes we'll attempt to send in a single
// envelope. 95 KB compressed → ~127 KB base64 — stays under the 128 KB API
// Gateway per-message limit with room for the envelope keys. Anything over
// this fails fast with PayloadTooLargeError so the caller can show a clear
// message rather than letting API Gateway drop the connection silently.
export const MAX_COMPRESSED_BYTES = 95 * 1024;

export interface CompressedFrame {
  encoding: "gzip-b64";
  payload: string;
  traceId?: string;
}

export class PayloadTooLargeError extends Error {
  rawBytes: number;
  compressedBytes: number;
  constructor(rawBytes: number, compressedBytes: number) {
    super(
      `WS payload too large: ${rawBytes} bytes raw / ${compressedBytes} bytes compressed exceeds the ${MAX_COMPRESSED_BYTES}-byte ceiling. Start a new chat to reset context.`
    );
    this.name = "PayloadTooLargeError";
    this.rawBytes = rawBytes;
    this.compressedBytes = compressedBytes;
  }
}

// btoa requires a binary string and chokes on very large inputs via String.fromCharCode(...spread).
// Walk the byte array in fixed slices to keep the argument list small.
function bytesToBase64(bytes: Uint8Array): string {
  const SLICE = 0x8000; // 32 KB
  let binary = "";
  for (let i = 0; i < bytes.length; i += SLICE) {
    const slice = bytes.subarray(i, Math.min(i + SLICE, bytes.length));
    binary += String.fromCharCode.apply(null, Array.from(slice));
  }
  return btoa(binary);
}

export interface EncodeResult {
  frame: any;            // the actual JSON object to send via sendMessage
  rawBytes: number;
  compressedBytes?: number;
  passthrough: boolean;  // true when below SMALL_PAYLOAD_THRESHOLD (no envelope)
}

export function encodeBody(message: any): EncodeResult {
  const json = JSON.stringify(message);
  const rawBytes = json.length;

  // Tiny payload: pass through. Backward compatible — backend gets the same
  // shape it always did.
  if (rawBytes < SMALL_PAYLOAD_THRESHOLD) {
    return {
      frame: message,
      rawBytes,
      passthrough: true,
    };
  }

  // eslint-disable-next-line no-console
  console.log("[wsPayloadCodec] raw payload before gzip", { rawBytes, message });

  const compressed = pako.gzip(json);
  const compressedBytes = compressed.length;

  if (compressedBytes > MAX_COMPRESSED_BYTES) {
    throw new PayloadTooLargeError(rawBytes, compressedBytes);
  }

  // Preserve traceId at the envelope level so backend can correlate logs even
  // before decompression.
  const traceId = (message as any)?.traceId;
  const frame: CompressedFrame = {
    encoding: "gzip-b64",
    payload: bytesToBase64(compressed),
    ...(traceId ? { traceId } : {}),
  };
  return {
    frame,
    rawBytes,
    compressedBytes,
    passthrough: false,
  };
}

// Inbound decoder (server → client). Inverse of the backend
// `LLM-function/services/wsPayloadCodec.js` `encodeOutgoing`.
//
// The backend gzip+base64-compresses large frames (multi-city hotel/flight/activity
// result blocks) so they fit API Gateway's 128 KB per-message ceiling instead of
// 413ing and hanging the UI. Detect the `{ encoding: "gzip-b64", payload }` envelope
// and inflate it back to the original JSON string. NON-envelope frames pass through
// untouched, so every `lastMessage` consumer is byte-for-byte unaffected when the
// backend flag is off — this decoder is always-on and backward compatible.
function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

export function decodeIncomingData(raw: any): any {
  // Cheap prefilter: only a string carrying the envelope marker can be compressed,
  // so we skip JSON.parse on the overwhelming majority (plain) frames.
  if (typeof raw !== "string" || raw.indexOf('"gzip-b64"') === -1) return raw;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && parsed.encoding === "gzip-b64" && typeof parsed.payload === "string") {
      return pako.ungzip(base64ToBytes(parsed.payload), { to: "string" });
    }
  } catch {
    /* not a valid envelope — fall through to passthrough */
  }
  return raw;
}
